import { Box, Typography, Button } from "@mui/material";
import InboxOutlinedIcon from "@mui/icons-material/InboxOutlined";

/**
 * EmptyState — Phase 2 reusable component.
 *
 * A consistent "nothing here yet" panel for tables/lists across the
 * redesigned pages, replacing whatever ad-hoc empty-row/empty-message
 * markup each page currently uses (to be swapped in per-page in later
 * phases — this component is not wired into any page yet).
 *
 * Props:
 *   icon        - optional custom icon element (defaults to InboxOutlinedIcon)
 *   title       - short headline, e.g. "No candidates found" (required)
 *   description - optional supporting line
 *   actionLabel - optional button label (e.g. "Add candidate")
 *   onAction    - optional button click handler; button only renders if
 *                 both actionLabel and onAction are provided
 */
export default function EmptyState({
  icon,
  title,
  description,
  actionLabel,
  onAction,
}) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        textAlign: "center",
        py: 6,
        px: 3,
        color: "text.secondary",
      }}
    >
      <Box
        sx={{
          width: 56,
          height: 56,
          borderRadius: "50%",
          bgcolor: "#f1f5f9",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          mb: 2,
          color: "#94a3b8",
        }}
      >
        {icon || <InboxOutlinedIcon sx={{ fontSize: 28 }} />}
      </Box>

      <Typography sx={{ fontWeight: 700, fontSize: 15, color: "text.primary" }}>
        {title}
      </Typography>

      {description && (
        <Typography sx={{ fontSize: 13, mt: 0.5, maxWidth: 360 }}>
          {description}
        </Typography>
      )}

      {actionLabel && onAction && (
        <Button
          variant="contained"
          size="small"
          onClick={onAction}
          sx={{ mt: 2.5 }}
        >
          {actionLabel}
        </Button>
      )}
    </Box>
  );
}
